import { useEffect, useState } from 'react';
import { ArrowRight, LockKeyhole, Pause, Play, X } from 'lucide-react';
import { ConstructionOsLogo } from '@/components/brand/ConstructionOsLogo';
import './cinematic-welcome.css';

const phases = [
  {
    title: 'Company & access',
    steps: 'Steps 01–02',
    description: 'Define the company workspace and review who can use it.',
    details: [
      ['Company profile', 'Enter your company details and choose your business priorities.'],
      ['People & roles', 'Add your roster, identify app users and assign seats and roles.'],
      ['Access approval', 'Review named users and their permissions before launch.'],
    ],
    outcome: 'A company profile and reviewed access roster.',
  },
  {
    title: 'Connections & imports',
    steps: 'Steps 03–04',
    description: 'Choose your providers and review the source of company records.',
    details: [
      ['Provider connections', 'Select your tools and connect the correct company accounts.'],
      ['Source review', 'Check provider identities, accounting authority and duplicate risks.'],
      ['Import decision', 'Start empty or import later through reviewed module workflows.'],
    ],
    outcome: 'Reviewed source decisions, with no automatic bulk import.',
  },
  {
    title: 'Review & launch',
    steps: 'Step 05',
    description: 'Review saved decisions and resolve the requirements for activation.',
    details: [
      ['Launch requirements', 'Check the saved company, access and source review decisions.'],
      ['AI consent', 'Review consent before using the setup assistant.'],
      ['Activation', 'Resolve outstanding requirements, then activate your workspace.'],
    ],
    outcome: 'An activated workspace after the required reviews pass.',
  },
] as const;

const steps = ['Company', 'People & access', 'Connections', 'Import review', 'Launch'];
interface CinematicWelcomeProps {
  verified: boolean;
  onBegin: () => void;
  onClose?: () => void;
}

/** Setup orientation only; saved progress and activation remain server-authorized. */
export function CinematicWelcome({ verified, onBegin, onClose }: CinematicWelcomeProps) {
  const [phase, setPhase] = useState(0);
  const [paused, setPaused] = useState(false);
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduced(preference.matches);
    update();
    preference.addEventListener?.('change', update);
    return () => preference.removeEventListener?.('change', update);
  }, []);
  const selected = phases[phase]!;
  return (
    <main className="helios-arrival" data-paused={paused || reduced}>
      <div className="helios-arrival__scene" aria-hidden="true" />
      <div className="helios-arrival__doors" aria-hidden="true">
        <span />
        <span />
      </div>
      <header className="helios-arrival__header">
        <div className="helios-arrival__brand">
          <ConstructionOsLogo variant="toolbar" />
          <span>
            HELIOS<small>CONSTRUCTION OS</small>
          </span>
        </div>
        <div className="helios-arrival__utilities">
          <span>
            <LockKeyhole size={14} aria-hidden="true" />
            {verified ? 'Authenticated session' : 'Private workspace'}
          </span>
          {onClose && (
            <button type="button" onClick={onClose} aria-label="Close introduction">
              <X size={18} />
            </button>
          )}
        </div>
      </header>
      <section className="helios-arrival__workspace" aria-labelledby="arrival-title">
        <div className="helios-arrival__overview">
          <span className="helios-arrival__eyebrow">COMPANY ONBOARDING / 5 STEPS</span>
          <h1 id="arrival-title">Your company setup plan</h1>
          <p>Configure your company, review access and data sources, then launch your workspace.</p>
          <ol className="helios-arrival__steps" aria-label="Your setup journey">
            {steps.map((label, index) => (
              <li key={label}>
                <span>0{index + 1}</span>
                {label}
              </li>
            ))}
          </ol>
          <div className="helios-arrival__start">
            <button type="button" className="helios-arrival__begin" onClick={onBegin}>
              Begin initialization <ArrowRight size={17} aria-hidden="true" />
            </button>
            <small>
              {verified
                ? 'Continue to your company setup directory.'
                : 'Next: verify your owner invitation or sign in to resume.'}
            </small>
          </div>
        </div>
        <section
          id="arrival-phase-checklist"
          className="helios-arrival__detail"
          aria-labelledby="arrival-phase-title"
          aria-live="polite"
        >
          <span className="helios-arrival__eyebrow">{selected.steps} / WHAT YOU WILL REVIEW</span>
          <h2 id="arrival-phase-title">{selected.title}</h2>
          <dl>
            {selected.details.map(([label, description]) => (
              <div key={label}>
                <dt>{label}</dt>
                <dd>{description}</dd>
              </div>
            ))}
          </dl>
          <p className="helios-arrival__outcome">
            <strong>Outcome</strong>
            {selected.outcome}
          </p>
        </section>
      </section>
      <section className="helios-arrival__plan" aria-labelledby="arrival-plan-title">
        <div className="helios-arrival__plan-heading">
          <h2 id="arrival-plan-title">Onboarding requirements</h2>
          <span>Select a phase to see its review checklist</span>
        </div>
        <div className="helios-arrival__phases">
          {phases.map((item, index) => (
            <button
              key={item.title}
              type="button"
              className="helios-arrival__phase"
              aria-pressed={phase === index}
              aria-controls="arrival-phase-checklist"
              onClick={() => setPhase(index)}
            >
              <span className="helios-arrival__eyebrow">{item.steps}</span>
              <strong>
                {item.title}
                <ArrowRight size={15} aria-hidden="true" />
              </strong>
              <span>{item.description}</span>
              <small>{item.details.map(([label]) => label).join(' · ')}</small>
            </button>
          ))}
        </div>
      </section>
      <footer className="helios-arrival__footer">
        <span>Setup overview · Your saved progress appears after sign-in.</span>
        <button
          type="button"
          className="helios-arrival__motion"
          onClick={() => setPaused(!paused)}
          disabled={reduced}
          aria-label={
            reduced ? 'Reduced motion enabled' : paused ? 'Resume animation' : 'Pause animation'
          }
        >
          {paused || reduced ? <Play size={13} /> : <Pause size={13} />} Background motion
        </button>
      </footer>
    </main>
  );
}
